import React from "react";
import { X, TrendingUp } from "lucide-react";
import MemberPointsChart from "../ui/MemberPointsChart";
import { MemberHistory, RankingUser } from "../../types";

interface MemberChartModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedUser: RankingUser | null;
  rankings: RankingUser[];
}

const CHART_COLORS = [
  "#2563eb",
  "#06b6d4",
  "#14b8a6",
  "#f97316",
  "#8b5cf6",
  "#e11d48",
];

const MemberChartModal: React.FC<MemberChartModalProps> = ({
  isOpen,
  onClose,
  selectedUser,
  rankings,
}) => {
  if (!isOpen || !selectedUser) return null;

  const selectedMonthlyPoints = selectedUser.monthlyPoints || [];

  const monthlyEventMap = new Map<
    string,
    Array<{ event_name: string; point: number }>
  >();
  (selectedUser.eventAcquiredPoints || []).forEach((item) => {
    const date = new Date(item.date);
    const month = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
    const existing = monthlyEventMap.get(month) || [];
    monthlyEventMap.set(month, [
      ...existing,
      { event_name: item.event_name, point: item.point },
    ]);
  });

  const allMonths = Array.from(
    new Set(
      rankings.flatMap((ranking) =>
        ranking.monthlyPoints.map((point) => point.month),
      ),
    ),
  ).sort((left, right) => left.localeCompare(right));

  const formatMonth = (month: string) => {
    const [year, monthNum] = month.split("-");
    return `${year}年${parseInt(monthNum, 10)}月`;
  };

  const histories: MemberHistory[] = rankings.map((ranking, index) => {
    const monthlyMap = new Map(
      ranking.monthlyPoints.map((point) => [point.month, point.points]),
    );
    let cumulative = 0;

    return {
      name: ranking.name,
      color: ranking.color ?? CHART_COLORS[index % CHART_COLORS.length],
      data: allMonths.map((month) => ({
        month,
        points: (cumulative += monthlyMap.get(month) ?? 0),
      })),
    };
  });

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center bg-slate-900/50 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={onClose}
    >
      <div
        className="bg-white w-full rounded-t-[22px] shadow-2xl animate-in slide-in-from-bottom duration-300 max-h-[85vh] overflow-y-auto scrollbar-hide"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 bg-slate-200 rounded-full" />
        </div>

        <div className="flex items-center justify-between px-6 pt-3 pb-4">
          <div className="flex items-center gap-3">
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center font-black text-sm text-white shadow-sm"
              style={{ backgroundColor: "#64748b" }}
            >
              {selectedUser.rank}
            </div>
            <div>
              <p className="font-black text-[15px] text-slate-800">
                {selectedUser.name}
              </p>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                {selectedUser.rank}位 ・ {selectedUser.points.toLocaleString()}{" "}
                pt
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 text-slate-500 hover:bg-slate-200 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex items-center gap-1.5 px-6 pb-3">
          <TrendingUp className="w-3.5 h-3.5 text-blue-400" />
          <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest">
            年間累計ポイント推移
          </span>
        </div>

        <div className="px-6 pb-6">
          {selectedMonthlyPoints.length === 0 || allMonths.length === 0 ? (
            <div className="bg-slate-50 rounded-[14px] p-4 text-center text-slate-500 text-xs font-bold">
              月次ポイント推移データは現在提供されていません。
            </div>
          ) : (
            <div className="bg-slate-50 rounded-[14px] p-4">
              <div className="rounded-xl border border-slate-200 bg-white px-3 py-4 shadow-sm">
                <MemberPointsChart
                  histories={histories}
                  selectedName={selectedUser.name}
                />
              </div>

              <div className="mt-3 border-t border-slate-200 pt-3">
                <p className="mb-2 px-1 text-[10px] font-bold uppercase tracking-widest text-blue-700">
                  月毎のポイント獲得数
                </p>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {selectedMonthlyPoints.map((item) => {
                    const events = monthlyEventMap.get(item.month) || [];
                    return (
                      <div key={`table-${item.month}`}>
                        <div className="text-xs">
                          <span className="font-bold text-slate-600">
                            {formatMonth(item.month)}
                          </span>
                        </div>
                        {events.length > 0 && (
                          <div className="mt-1 space-y-0.5">
                            {events.map((event, i) => (
                              <div
                                key={i}
                                className="flex justify-between items-center pl-4 text-[10px] text-slate-400"
                              >
                                <span className="font-medium truncate max-w-[180px]">
                                  {event.event_name}
                                </span>
                                <span className="font-bold text-slate-500 shrink-0">
                                  +{event.point}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="mt-4 flex flex-wrap gap-2 border-t border-slate-200 pt-3 text-[10px] font-bold text-slate-500">
                {histories.map((history) => {
                  const isSelected = history.name === selectedUser.name;

                  return (
                    <div
                      key={history.name}
                      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 ${
                        isSelected ? "bg-blue-50 text-blue-700" : "bg-slate-100"
                      }`}
                    >
                      <span
                        className="h-2.5 w-2.5 rounded-full"
                        style={{
                          backgroundColor: history.color,
                          opacity: isSelected ? 1 : 0.4,
                        }}
                      />
                      {history.name}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MemberChartModal;
